import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  FileText, 
  Download, 
  TrendingUp,
  DollarSign,
  Smartphone,
  Users,
  BarChart3,
  Mail,
  Send,
  CheckCircle,
  ExternalLink,
  Plus
} from 'lucide-react';

const Reports = () => {
  const { user } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [selectedType, setSelectedType] = useState('all');
  const [isGenerating, setIsGenerating] = useState(false);

  // Rol tabanlı yetkilendirme fonksiyonları
  const canViewReports = () => {
    if (!user) return false;
    return user.role === 'yonetici' || 
           user.role === 'alt_yonetici' || 
           user.role === 'analizci' ||
           user.permissions?.reports?.view;
  };

  const canGenerateReports = () => {
    if (!user) return false;
    return user.role === 'yonetici' || 
           user.role === 'analizci' ||
           user.permissions?.reports?.generate;
  };
  const [emailAddress, setEmailAddress] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [summaryStats, setSummaryStats] = useState(null);
  const [recentReports, setRecentReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatedReport, setGeneratedReport] = useState(null);

  useEffect(() => {
    fetchReportsData();
  }, []);

  const fetchReportsData = async () => {
    try {
      setLoading(true);
      
      // Önce uygulamaları kontrol et
      const appsResponse = await axios.get('/apps');
      const hasApps = appsResponse.data.success && appsResponse.data.apps && appsResponse.data.apps.length > 0;
      
      if (!hasApps) {
        // Uygulama yoksa boş state göster
        setSummaryStats({
          totalRevenue: 0,
          totalApps: 0,
          totalUsers: 0,
          avgRating: 0,
          monthlyGrowth: 0,
          topApp: null,
          teamSize: 0,
          activeApps: 0,
          hasApps: false
        });
        setRecentReports([]);
        setLoading(false);
        return;
      }

      // Özet istatistikleri çek
      const summaryResponse = await axios.get('/reports/summary');
      if (summaryResponse.data.success) {
        setSummaryStats({
          ...summaryResponse.data.summary,
          hasApps: true
        });
      }

      // Son raporları çek
      const reportsResponse = await axios.get('/reports');
      if (reportsResponse.data.success) {
        setRecentReports(reportsResponse.data.reports);
      }
    } catch (error) {
      console.error('Rapor verileri hatası:', error);
      // Hata durumunda boş state
      setSummaryStats({
        totalRevenue: 0,
        totalApps: 0,
        totalUsers: 0,
        avgRating: 0,
        monthlyGrowth: 0,
        topApp: null,
        teamSize: 0,
        activeApps: 0,
        hasApps: false
      });
      setRecentReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async (reportType) => {
    // Uygulama kontrolü
    if (!summaryStats?.hasApps) {
      alert('Rapor oluşturmak için önce en az bir uygulama eklemeniz gerekiyor!');
      return;
    }

    setIsGenerating(true);
    setGeneratedReport(null);
    
    try {
      const response = await axios.post('/reports/generate', {
        type: selectedType,
        period: selectedPeriod,
        format: 'pdf'
      });

      if (response.data.success) {
        // Başarılı rapor oluşturma
        const report = response.data.report;
        setGeneratedReport(report);
        
        // Recent reports listesine ekle
        setRecentReports(prev => [report, ...prev.slice(0, 9)]);
        
        // 10 saniye sonra success mesajını temizle
        setTimeout(() => {
          setGeneratedReport(null);
        }, 10000);
      }
    } catch (error) {
      console.error('Rapor oluşturma hatası:', error);
      alert('Rapor oluşturulurken hata oluştu!');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadReport = async (reportId) => {
    try {
      const response = await axios.get(`/reports/${reportId}/download`);
      if (response.data.success) {
        // Gerçek uygulamada dosya indirme
        window.open(response.data.downloadUrl, '_blank');
      }
    } catch (error) {
      console.error('Rapor indirme hatası:', error);
      alert('Rapor indirilirken hata oluştu!');
    }
  };

  const handleSendWeeklyReport = async () => {
    if (!emailAddress) {
      alert('Lütfen email adresi girin!');
      return;
    }

    setIsSendingEmail(true);
    try {
      const response = await axios.post('/send-weekly-report', {
        email: emailAddress
      });

      if (response.data.success) {
        alert('Haftalık rapor başarıyla gönderildi!');
        setEmailAddress('');
      }
    } catch (error) {
      console.error('Email gönderme hatası:', error);
      alert('Email gönderilirken hata oluştu!');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleTestEmail = async () => {
    if (!emailAddress) {
      alert('Lütfen email adresi girin!');
      return;
    }

    try {
      const response = await axios.post('/test-email', {
        email: emailAddress
      });

      if (response.data.success) {
        alert('Test email başarıyla gönderildi!');
      }
    } catch (error) {
      console.error('Test email hatası:', error);
      alert('Test email gönderilirken hata oluştu!');
    }
  };

  const reportTypes = [
    {
      id: 'revenue',
      name: 'Gelir Raporu',
      description: 'Detaylı gelir analizi ve trendler',
      icon: DollarSign,
      color: 'bg-green-500'
    },
    {
      id: 'apps',
      name: 'Uygulama Performansı',
      description: 'Uygulama bazlı performans metrikleri',
      icon: Smartphone,
      color: 'bg-blue-500'
    },
    {
      id: 'users',
      name: 'Kullanıcı Analizi',
      description: 'Kullanıcı davranışları ve demografik veriler',
      icon: Users,
      color: 'bg-purple-500'
    },
    {
      id: 'team',
      name: 'Ekip Performansı',
      description: 'Ekip üyelerinin katkı ve performans analizi',
      icon: BarChart3,
      color: 'bg-orange-500'
    }
  ];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  // Yetki kontrolü
  if (!canViewReports()) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FileText className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-red-800">
                Erişim Yetkisi Yok
              </h3>
              <p className="mt-2 text-sm text-red-700">
                Raporlar sayfasını görüntülemek için gerekli yetkiniz bulunmuyor. 
                Lütfen yöneticinizle iletişime geçin.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Raporlar</h1>
        <p className="mt-2 text-sm text-gray-700">
          Detaylı analiz raporları oluşturun ve indirin
        </p>
      </div>

      {/* Uygulama Yok Uyarısı */}
      {summaryStats && !summaryStats.hasApps && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Smartphone className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-yellow-800">
                Henüz Uygulama Eklenmemiş
              </h3>
              <p className="mt-2 text-sm text-yellow-700">
                Rapor oluşturabilmek için önce en az bir mobil uygulama eklemeniz gerekiyor. 
                Uygulamalar sayfasından yeni uygulama ekleyebilirsiniz.
              </p>
              <div className="mt-4">
                <button
                  onClick={() => window.location.href = '/apps'}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Uygulama Ekle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Özet İstatistikler */}
      {summaryStats && summaryStats.hasApps && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DollarSign className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Toplam Gelir</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      ₺{summaryStats.totalRevenue?.toLocaleString('tr-TR') || '0'}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Smartphone className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Aktif Uygulama</dt>
                    <dd className="text-lg font-medium text-gray-900">{summaryStats.activeApps || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Toplam Kullanıcı</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {summaryStats.totalUsers?.toLocaleString('tr-TR') || '0'}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-8 w-8 text-orange-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Aylık Büyüme</dt>
                    <dd className="text-lg font-medium text-gray-900">%{summaryStats.monthlyGrowth || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Raporu Gönderimi */}
      {summaryStats?.hasApps && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              📧 Haftalık Rapor Gönderimi
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Haftalık performans raporunu email olarak gönderin. Sistem otomatik olarak her Pazartesi 09:00'da rapor gönderir.
            </p>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Adresi
                </label>
                <input
                  type="email"
                  placeholder="ornek@email.com"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                />
              </div>
              
              <div className="flex items-end space-x-2">
                <button
                  onClick={handleSendWeeklyReport}
                  disabled={isSendingEmail}
                  className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {isSendingEmail ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Gönder
                    </>
                  )}
                </button>
                
                <button
                  onClick={handleTestEmail}
                  className="px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  title="Test Email"
                >
                  <Mail className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rapor Oluşturma - Sadece uygulama varsa ve yetki varsa */}
      {summaryStats?.hasApps && canGenerateReports() && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Yeni Rapor Oluştur
            </h3>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rapor Türü
                </label>
                <select
                  className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                >
                  <option value="all">Genel Rapor</option>
                  <option value="revenue">Gelir Raporu</option>
                  <option value="apps">Uygulama Raporu</option>
                  <option value="users">Kullanıcı Raporu</option>
                  <option value="team">Ekip Raporu</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zaman Aralığı
                </label>
                <select
                  className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                >
                  <option value="weekly">Haftalık</option>
                  <option value="monthly">Aylık</option>
                  <option value="quarterly">Çeyreklik</option>
                  <option value="yearly">Yıllık</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={() => handleGenerateReport(selectedType)}
                  disabled={isGenerating}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {isGenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Oluşturuluyor...
                    </>
                  ) : (
                    <>
                      <FileText className="h-4 w-4 mr-2" />
                      Rapor Oluştur
                    </>
                  )}
                </button>
              </div>
            </div>
            
            {/* Başarılı Rapor Oluşturma Bildirimi */}
            {generatedReport && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-green-800">
                      Rapor Başarıyla Oluşturuldu!
                    </h3>
                    <div className="mt-2 text-sm text-green-700">
                      <p><strong>{generatedReport.name}</strong> raporu hazır.</p>
                      <p className="text-xs text-green-600 mt-1">
                        Boyut: {generatedReport.size} | Format: {generatedReport.format}
                      </p>
                    </div>
                    <div className="mt-3">
                      <button
                        onClick={() => handleDownloadReport(generatedReport.id)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Raporu İndir
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Rapor Türleri - Sadece uygulama varsa */}
      {summaryStats?.hasApps && (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {reportTypes.map((report) => (
            <div key={report.id} className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow cursor-pointer">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`p-3 rounded-md ${report.color}`}>
                      <report.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{report.name}</dt>
                      <dd className="text-xs text-gray-400 mt-1">{report.description}</dd>
                    </dl>
                  </div>
                </div>
                <div className="mt-4">
                  <button
                    onClick={() => {
                      setSelectedType(report.id);
                      handleGenerateReport(report.name);
                    }}
                    disabled={isGenerating}
                    className="w-full text-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {isGenerating ? 'Oluşturuluyor...' : 'Hızlı Oluştur'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Son Raporlar */}
      {recentReports.length > 0 && (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Son Oluşturulan Raporlar
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Daha önce oluşturduğunuz raporları indirin
            </p>
          </div>
          <ul className="divide-y divide-gray-200">
            {recentReports.map((report) => (
              <li key={report.id} className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                        <FileText className="h-6 w-6 text-gray-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{report.name}</div>
                      <div className="text-sm text-gray-500">
                        {report.type} • {report.format} • {report.size}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {formatDate(report.generatedAt)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={() => handleDownloadReport(report.id)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      İndir
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Hızlı İstatistikler */}
      {summaryStats && summaryStats.hasApps && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Bu Ay Özet
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  ₺{((summaryStats.totalRevenue || 0) * 0.15).toLocaleString('tr-TR')}
                </div>
                <div className="text-sm text-gray-500">Bu Ay Gelir</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{summaryStats.avgRating || '0'}</div>
                <div className="text-sm text-gray-500">Ortalama Puan</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{summaryStats.topApp || 'N/A'}</div>
                <div className="text-sm text-gray-500">En İyi Uygulama</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;